import Logo from '@/assets/images/logo.svg'
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button'
import { Center } from '@/components/ui/center'
import { Checkbox, CheckboxIcon, CheckboxIndicator, CheckboxLabel } from '@/components/ui/checkbox'
import {
  Drawer,
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from '@/components/ui/drawer'
import { Heading } from '@/components/ui/heading'
import { HStack } from '@/components/ui/hstack'
import { Icon } from '@/components/ui/icon'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import useDefaultToast from '@/components/useDefaultToast'
import { isWechatAvailable } from '@/lib/wechat'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useIsFocused } from "expo-router/react-navigation"
import { Link, Redirect, useRouter, type Href } from 'expo-router'
import { CheckIcon, XIcon } from 'lucide-react-native'
import { useEffect, useRef, useState, type PropsWithChildren } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context'
import handleWechatLogin from './handleWechatLogin'

export default function Login() {
  const { t } = useTranslation('login')
  const router = useRouter()
  const dispatch = useAppDispatch()
  const toast = useDefaultToast()

  const [wechatAvailable, setWechatAvailable] = useState(false)
  useEffect(() => {
    isWechatAvailable().then(setWechatAvailable)
  }, [])

  const signedIn = useAppSelector(state => state.session.signedIn)
  const [agreed, setAgreed] = useState(false)
  const [loggingIn, setLoggingIn] = useState(false)
  const [agreementModalOpen, setAgreementModalOpen] = useState(false)

  const pendingProviderCallback = useRef<null | (() => void)>(null)

  if (signedIn) {
    return <Redirect href="/forms" />
  }

  const mustAgree = (callback: () => void) => () => {
    if (agreed) {
      callback()
      return
    }
    pendingProviderCallback.current = callback
    setAgreementModalOpen(true)
  }

  const handleWxLogin = mustAgree(() => handleWechatLogin({ toast, dispatch, router, t, setLoggingIn }))

  const handleMobileLogin = mustAgree(() => {
    router.push('/login/sms')
  })

  const handleIdentityLogin = mustAgree(() => {
    router.push('/login/identity')
  })

  const handleAgreementModalOk = () => {
    setAgreed(true)
    pendingProviderCallback.current?.()
  }

  const handleAgreementModalClose = () => {
    setAgreementModalOpen(false)
    pendingProviderCallback.current = null
  }

  return (
    <Center className="px-4 py-10 min-h-[80%]">
      <VStack space="sm" className="items-center mb-8">
        <Logo height={40} />
        <Text size="footnote" className="text-typography-600">
          {t('slogan')}
        </Text>
      </VStack>
      <VStack space="lg" className="p-6 max-w-[540] w-full">
        {wechatAvailable && (
          <Button size="lg" onPress={handleWxLogin} isDisabled={loggingIn}>
            {loggingIn && <ButtonSpinner />}
            <ButtonText>{loggingIn ? t('wxLogin.loggingIn') : t('wxLogin.title')}</ButtonText>
          </Button>
        )}
        <Button variant="outline" action="secondary" size="lg" onPress={handleMobileLogin} isDisabled={loggingIn}>
          <ButtonText>{t('mobileLogin.title')}</ButtonText>
        </Button>
        <Button variant="outline" action="secondary" size="lg" onPress={handleIdentityLogin} isDisabled={loggingIn}>
          <ButtonText>{t('identityLogin.title')}</ButtonText>
        </Button>
        <Checkbox value="agreed" isChecked={agreed} onChange={setAgreed}>
          <CheckboxIndicator className="border-typography-600">
            <CheckboxIcon
              as={CheckIcon}
            />
          </CheckboxIndicator>
          <CheckboxLabel>
            <Trans
              t={t}
              i18nKey="agreement"
              components={{
                terms: <AgreementLink href="/terms" />,
                privacy: <AgreementLink href="/privacy" />,
                usage: <AgreementLink href="/usage_specification" />,
              }}
            />
          </CheckboxLabel>
        </Checkbox>
      </VStack>
      <AgreementModal open={agreementModalOpen} onClose={handleAgreementModalClose} onOk={handleAgreementModalOk} />
    </Center>
  )
}

function AgreementLink({ href, children, onPress }: PropsWithChildren<{ href: Href; onPress?: () => void }>) {
  return (
    <Link href={href} onPress={onPress}>
      <Text className="text-primary-500">{children}</Text>
    </Link>
  )
}

type AgreementModalProps = {
  open: boolean
  onClose: () => void
  onOk: () => void
}

function AgreementModal({ open, onClose, onOk }: AgreementModalProps) {
  // 只有在当前页面处于 focus 状态，才显示 modal
  const isFocused = useIsFocused()

  return (
    <Drawer isOpen={isFocused && open} onClose={onClose} anchor="bottom">
      <DrawerBackdrop />
      <DrawerContent className="h-auto">
        <SafeAreaProvider>
          <SafeAreaView>
            <DrawerHeader>
              <Heading size="md">请阅读并同意以下条款</Heading>
              <DrawerCloseButton>
                <Icon as={XIcon} size="xl" />
              </DrawerCloseButton>
            </DrawerHeader>
            <DrawerBody>
              <HStack space="md" className="justify-center">
                <AgreementLink href="/terms">《使用条款》</AgreementLink>
                <AgreementLink href="/privacy">《隐私政策》</AgreementLink>
              </HStack>
            </DrawerBody>
            <DrawerFooter>
              <Button
                onPress={() => {
                  onOk()
                  onClose()
                }}
                className="flex-1"
              >
                <ButtonText>同意并继续</ButtonText>
              </Button>
            </DrawerFooter>
          </SafeAreaView>
        </SafeAreaProvider>
      </DrawerContent>
    </Drawer>
  )
}
